import { blobCenter } from './blobGeometry';
import type { LocateMatch, ResultOrdering } from './LocateTypes';

/**
 * Spatial center used for scan-order sorting. The SAM2 contour is the source of
 * truth when present (axis-aligned center of the segment), falling back to the
 * detector box center when the match has no segment.
 */
function matchCenter(m: LocateMatch): { x: number; y: number } {
  return m.segment
    ? blobCenter(m.segment)
    : { x: m.detection.x, y: m.detection.y };
}

function getComparator(
  ordering: ResultOrdering,
): (a: LocateMatch, b: LocateMatch) => number {
  switch (ordering) {
    // Scan order — sort by distance from a corner (derived center)
    case 'top-left-to-bottom-right':
      return (a, b) => {
        const ca = matchCenter(a);
        const cb = matchCenter(b);

        return ca.x + ca.y - (cb.x + cb.y);
      };

    case 'bottom-right-to-top-left':
      return (a, b) => {
        const ca = matchCenter(a);
        const cb = matchCenter(b);

        return cb.x + cb.y - (ca.x + ca.y);
      };

    case 'top-right-to-bottom-left':
      return (a, b) => {
        const ca = matchCenter(a);
        const cb = matchCenter(b);

        return cb.x - ca.x + (ca.y - cb.y);
      };

    case 'bottom-left-to-top-right':
      return (a, b) => {
        const ca = matchCenter(a);
        const cb = matchCenter(b);

        return ca.x - cb.x + (cb.y - ca.y);
      };

    // By property (descending)
    case 'confidence-desc':
      return (a, b) => b.detection.score - a.detection.score;

    case 'area-desc':
      return (a, b) => (b.segment?.area ?? 0) - (a.segment?.area ?? 0);
  }
}

/**
 * Sort matches by the given ordering strategy.
 * Returns a new sorted array (does not mutate the input).
 * When ordering is undefined, falls back to score descending (legacy behavior).
 */
export function sortBlobs(
  matches: LocateMatch[],
  ordering: ResultOrdering | undefined,
): LocateMatch[] {
  const effectiveOrdering: ResultOrdering = ordering ?? 'confidence-desc';

  if (matches.length <= 1) {
    return [...matches];
  }

  const sorted = [...matches];

  sorted.sort(getComparator(effectiveOrdering));

  return sorted;
}
